import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * Minimal user-preferences store backed by localStorage.
 *
 * API-compatible with the brandbot UserPreferencesService consumed by the
 * ported ThemeService (`get<T>(key)` / `patch(partial)`); swap the
 * implementation for a backend endpoint later without touching callers.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly STORAGE_KEY = 'pc-user-preferences';

  /** Read a single value from the stored preferences. */
  public get<T>(key: string): T | null {
    const prefs = this._readAll();
    return (prefs[key] as T) ?? null;
  }

  /** Merge the given keys into the stored preferences. */
  public patch(partial: Record<string, unknown>): Observable<void> {
    const prefs = { ...this._readAll(), ...partial };
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Storage full/unavailable — preferences just won't persist.
    }
    return of(undefined);
  }

  private _readAll(): Record<string, unknown> {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY) ?? '{}');
    } catch {
      return {};
    }
  }
}
